<template>
    <div>
        <div>
            {{ slotSchema.Name }}
        </div>
        <pc-table
            v-bind:empty-text="tableEmptyText"
            v-bind:items="memberItems"
            v-bind:columns="memberColumns"
            v-bind:initial-sort="[]"
            v-bind:items-per-page="10000"
            key-prop="ConnectionMemberId"
            hide-search>
            <template v-slot:DataType="member">
                <pc-select
                    class="minwidth-100"
                    v-bind:alt-label="fieldTypeSelectorLabel"
                    v-bind:model-value="member.DataType"
                    v-bind:options="memberDataTypeOptions"
                    v-bind:readonly="!canEditMemberSchema(member)"
                    v-bind:required="true"
                    v-bind:validator="getValidator(app, VALIDATOR_TYPES.required)"
                    value-prop="PublicId"
                    text-prop="Name"
                    v-on:update:model-value="setMemberDataType(member, $event)" />
            </template>
            <template v-slot:Name="member">
                <div class="d-flex">
                    <pc-input
                        class="flex-grow-1 minwidth-150"
                        v-bind:alt-label="memberSlotAliasLabel"
                        v-bind:model-value="member.Name"
                        v-bind:required="true"
                        v-bind:validator="memberNameValidator(member)"
                        v-bind:readonly="!canEditMemberSchema(member)"
                        v-bind:maxlength="255"
                        v-on:update:model-value="setTargetMemberName(member, $event)" />
                    <div>
                        <pc-button
                            v-if="canAddNewMember && slotSchema.IsSignal"
                            class="ms-2"
                            style="min-width: 30px"
                            v-bind:disabled="!hasMemberAction(member)"
                            v-bind:title="memberActionTooltipText(member)"
                            v-on:click="doMemberAction(member)">
                            <pc-bs-icon v-bind:name="memberActionIcon(member)" />
                        </pc-button>
                    </div>
                </div>
            </template>
            <template v-slot:MappedTo="member">
                <div class="d-flex">
                    <pc-select
                        class="flex-grow-1"
                        v-bind:alt-label="memberSpecificSlotLabel"
                        v-bind:model-value="member.MappedTo"
                        v-bind:options="memberMappedToOptions(member)"
                        value-prop="PublicId"
                        text-prop="Name"
                        v-on:update:model-value="setTargetMemberMappedTo(member, $event)">
                        <option value="">
                            {{ noneSelectedOptionLabel }}
                        </option>
                    </pc-select>
                    <pc-button
                        v-if="canAddNewMember"
                        class="ms-2"
                        style="min-width: 30px"
                        v-bind:disabled="!hasMemberAction(member)"
                        v-bind:title="memberActionTooltipText(member)"
                        v-on:click="doMemberAction(member)">
                        <pc-bs-icon v-bind:name="memberActionIcon(member)" />
                    </pc-button>
                </div>
            </template>
        </pc-table>
        <pc-button
            v-if="canAddNewMember"
            v-on:click="addNewMember">
            {{ addSlotButtonLabel }}
        </pc-button>
    </div>
</template>

<script setup>
    import { computed, inject } from "vue";
    import { PAGE_APP_SYMBOL, PC_RES_SYMBOL } from "_Main/injectionSymbols";
    import { useChannelStore } from "./channelStore.js";
    import { storeToRefs } from "pinia";
    import { DIRECTION_TYPES, SLOT_TYPES, NEW_MEMBER_ID_BASE } from "Types/channelTypes.js";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { KEYS, NAMESPACE } from "../i18n/programChannels.i18n";
    import { KEYS as ARCHIVE_KEYS, NAMESPACE as ARCHIVE_NAMESPACE } from "Util/i18n/archiveUtil.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { useResource } from "Util/i18n";

    const app = inject(PAGE_APP_SYMBOL);
    const res = inject(PC_RES_SYMBOL);

    const channelStore = useChannelStore();
    const { channelInUse, isEditingSchema, isPublishing, programDataTypes, showArchived } = storeToRefs(channelStore);
    const {
        addMember,
        getMappingOptions,
        hasValidMemberName,
        removeMember,
        setMemberArchived,
        setMemberMappedTo,
        setMemberName,
        setMemberType
    } = channelStore;

    const {
        attachmentSlotName,
        dataSlotName,
        duplicateNameMessage,
        fieldTypeSelectorLabel,
        formSlotName,
        noFieldsButAddable,
        noneSelectedOptionLabel,
        nothingToConfigureText,
        roleSlotName,
        signalSlotName,
        statusSlotName
    } = useResource(KEYS, NAMESPACE);
    const { isArchivedHeader, archiveText, unarchiveText } = useResource(ARCHIVE_KEYS, ARCHIVE_NAMESPACE);
    const { trueText, deleteText } = useResource(COMMON_KEYS, COMMON_NAMESPACE);

    const props = defineProps({
        slotSchema: { type: Object, required: true },
        slotInstance: { type: Object, required: true },
        groupInstance: { type: Object, required: true }
    });

    const memberColumns = computed(() => {
        const baseCols = [];
        if (props.slotSchema.SlotType === SLOT_TYPES.Data) {
            baseCols.push({
                header: fieldTypeSelectorLabel.value,
                property: "DataType",
                showRequired: true
            });
        }
        baseCols.push({
            header: memberSlotAliasLabel.value,
            property: "Name",
            showRequired: true
        });
        if (!props.slotSchema.IsSignal && props.slotSchema.IsEditable) {
            baseCols.push({
                header: memberSpecificSlotLabel.value,
                property: "MappedTo"
            });
        }
        if (showArchived.value && props.slotSchema.CanArchiveFields) {
            baseCols.push({
                header: isArchivedHeader.value,
                property: "IsArchived",
                displayValue: (value) => (value ? trueText.value : "")
            });
        }
        return baseCols.map((bc) => {
            bc.visible = true;
            bc.orderable = false;
            return bc;
        });
    });

    const memberItems = computed(() => {
        if (!showArchived.value) {
            return props.slotInstance.Members.filter((m) => !m.IsArchived);
        }
        return props.slotInstance.Members;
    });

    const memberDataTypeOptions = computed(() => {
        if (props.slotSchema.FilterDataTypes) {
            return programDataTypes.value.filter((pdt) => props.slotSchema.FilterDataTypes.includes(pdt.PublicId));
        }
        return programDataTypes.value;
    });

    const tableEmptyText = computed(() =>
        isEditingSchema.value ? noFieldsButAddable.value : nothingToConfigureText.value
    );

    const slotTypeName = computed(() => {
        switch (props.slotSchema.SlotType) {
            case SLOT_TYPES.Data:
                return dataSlotName.value;
            case SLOT_TYPES.Attachment:
                return attachmentSlotName.value;
            case SLOT_TYPES.Role:
                return roleSlotName.value;
            case SLOT_TYPES.Form:
                return formSlotName.value;
            case SLOT_TYPES.Status:
                return statusSlotName.value;
            case SLOT_TYPES.SendSignalName:
            case SLOT_TYPES.ReceiveSignalName:
                return signalSlotName.value;
        }
        return "";
    });

    const memberSpecificSlotLabel = computed(() => {
        return res({
            namespace: NAMESPACE,
            key: KEYS.memberSpecificSlotLabel,
            values: { slotType: slotTypeName.value }
        });
    });

    const memberSlotAliasLabel = computed(() => {
        return res({
            namespace: NAMESPACE,
            key: KEYS.memberSlotAliasLabel,
            values: { slotType: slotTypeName.value }
        });
    });

    const addSlotButtonLabel = computed(() => {
        return res({
            namespace: NAMESPACE,
            key: KEYS.addSlotButtonLabel,
            values: { slotType: slotTypeName.value }
        });
    });

    const canAddNewMember = computed(() => {
        if (isEditingSchema.value && props.slotSchema.CanAddFieldsToExistingSchema) {
            return true;
        }
        return !channelInUse.value;
    });

    function addNewMember() {
        app.setUnsavedChanges(true);
        addMember(props.groupInstance.PublicId, props.slotSchema.SlotId);
    }

    function canEditMemberSchema(member) {
        return member.ConnectionMemberId.startsWith(NEW_MEMBER_ID_BASE) || !channelInUse.value;
    }

    function memberMappedToOptions(member) {
        return getMappingOptions(props.slotSchema.SlotType, member.DataType, allowReadOnlyOptions.value);
    }

    const allowReadOnlyOptions = computed(() => {
        // If we are copying from this program then allow readonly options
        return (
            (isPublishing.value && props.slotSchema.Direction === DIRECTION_TYPES.FromPublishingProgram) ||
            (!isPublishing.value && props.slotSchema.Direction === DIRECTION_TYPES.FromConsumingProgram)
        );
    });

    function memberNameValidator(member) {
        return getValidator(app, [
            VALIDATOR_TYPES.required,
            {
                type: VALIDATOR_TYPES.custom,
                validate: () => hasValidMemberName(member),
                errorMsg: duplicateNameMessage.value
            }
        ]);
    }

    function setMemberDataType(member, value) {
        if (value === member.DataType) {
            return;
        }
        app.setUnsavedChanges(true);
        setMemberType(member.ConnectionMemberId, value);
    }

    function setTargetMemberName(member, value) {
        if (value === member.Name) {
            return;
        }
        app.setUnsavedChanges(true);
        setMemberName(member.ConnectionMemberId, value);
    }

    function setTargetMemberMappedTo(member, value) {
        if (value === member.MappedTo) {
            return;
        }
        app.setUnsavedChanges(true);

        if (canEditMemberSchema(member)) {
            // Set the member name to the selected data field name if the member name is
            // empty or if it matches the previous data field name
            const memberOptions = memberMappedToOptions(member);
            const option = memberOptions.find((element) => element.PublicId === value);
            const optionName = option?.Name ?? "";
            const oldOption = memberOptions.find((e) => e.PublicId === member.MappedTo);
            if (!member.Name || (oldOption && member.Name === oldOption.Name)) {
                setMemberName(member.ConnectionMemberId, optionName);
            }
        }

        setMemberMappedTo(member.ConnectionMemberId, value);
    }

    function hasMemberAction(member) {
        if (props.slotSchema.IsRequired && memberItems.value.length <= 1) {
            return false;
        }
        if (isEditingSchema.value && props.slotSchema.CanArchiveFields) {
            return true;
        }
        return canEditMemberSchema(member);
    }

    function memberActionIcon(member) {
        if (props.slotSchema.CanArchiveFields && !member.ConnectionMemberId.startsWith(NEW_MEMBER_ID_BASE)) {
            return member.IsArchived ? "box-arrow-up" : "box-arrow-in-down";
        }
        return "x-lg";
    }

    function memberActionTooltipText(member) {
        if (props.slotSchema.CanArchiveFields && !member.ConnectionMemberId.startsWith(NEW_MEMBER_ID_BASE)) {
            return member.IsArchived ? unarchiveText.value : archiveText.value;
        }
        return deleteText.value;
    }

    function doMemberAction(member) {
        app.setUnsavedChanges(true);
        if (props.slotSchema.CanArchiveFields && !member.ConnectionMemberId.startsWith(NEW_MEMBER_ID_BASE)) {
            setMemberArchived(member.ConnectionMemberId, !member.IsArchived);
        } else {
            removeMember(member.ConnectionMemberId);
        }
    }
</script>

<style lang="scss" scoped>
    .minwidth-100 {
        min-width: 100px;
    }
    .minwidth-150 {
        min-width: 150px;
    }
</style>
